import { useMemo, useState } from "react";
import { Check, Copy, FileSpreadsheet, FileText, FileType, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Word } from "@/types";
import {
  exportAsDocx,
  exportAsPdf,
  exportAsXlsx,
  wordsToQuizletText,
} from "@/lib/exportWords";

interface ExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  words: Word[];
  /** Used for the download filename, e.g. the study set or category name */
  name: string;
}

/**
 * Export words as a Quizlet-ready tab-separated list.
 * Hebrew ⇥ English, one card per line — paste directly into
 * Quizlet's import ("Tab" between term/definition, "New line" between cards).
 */
export function ExportDialog({ open, onOpenChange, words, name }: ExportDialogProps) {
  const [copied, setCopied] = useState(false);
  const [exporting, setExporting] = useState<"xlsx" | "docx" | "pdf" | null>(null);

  const handleExport = async (format: "xlsx" | "docx" | "pdf") => {
    setExporting(format);
    try {
      if (format === "xlsx") await exportAsXlsx(words, name);
      else if (format === "docx") await exportAsDocx(words, name);
      else await exportAsPdf(words, name);
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setExporting(null);
    }
  };

  const { text, exported, skipped } = useMemo(
    () => wordsToQuizletText(words),
    [words]
  );

  const previewLines = useMemo(() => text.split("\n").slice(0, 5), [text]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Export for Quizlet</DialogTitle>
          <DialogDescription>
            {exported} word{exported === 1 ? "" : "s"} ready to export
            {skipped > 0 && ` (${skipped} without a translation skipped)`}.
          </DialogDescription>
        </DialogHeader>

        {exported > 0 ? (
          <>
            {/* Preview */}
            <div className="rounded-md border bg-muted/50 p-3 font-mono text-sm space-y-1 overflow-x-auto">
              {previewLines.map((line, i) => {
                const [hebrew, translation] = line.split("\t");
                return (
                  <div key={i} className="flex gap-4 whitespace-nowrap">
                    <span className="hebrew-text" dir="rtl">{hebrew}</span>
                    <span className="text-muted-foreground">→ {translation}</span>
                  </div>
                );
              })}
              {exported > previewLines.length && (
                <div className="text-muted-foreground text-xs pt-1">
                  …and {exported - previewLines.length} more
                </div>
              )}
            </div>

            {/* Download formats */}
            <div className="grid grid-cols-3 gap-2">
              <Button
                variant="outline"
                onClick={() => handleExport("xlsx")}
                disabled={exporting !== null}
              >
                {exporting === "xlsx" ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <FileSpreadsheet className="mr-2 h-4 w-4" />
                )}
                Excel
              </Button>
              <Button
                variant="outline"
                onClick={() => handleExport("docx")}
                disabled={exporting !== null}
              >
                {exporting === "docx" ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <FileText className="mr-2 h-4 w-4" />
                )}
                Word
              </Button>
              <Button
                variant="outline"
                onClick={() => handleExport("pdf")}
                disabled={exporting !== null}
              >
                {exporting === "pdf" ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <FileType className="mr-2 h-4 w-4" />
                )}
                PDF
              </Button>
            </div>

            <Button onClick={handleCopy} className="w-full">
              {copied ? (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="mr-2 h-4 w-4" />
                  Copy to Clipboard
                </>
              )}
            </Button>

            <p className="text-xs text-muted-foreground">
              Upload the <span className="font-medium">Excel</span> or{" "}
              <span className="font-medium">Word</span> file to Quizlet's import, or paste from
              the clipboard (Tab between term/definition, New line between cards). PDF is best
              for printing.
            </p>
          </>
        ) : (
          <p className="text-sm text-muted-foreground py-4 text-center">
            None of these words have translations to export yet.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
